import { useEffect } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { fetchMemberSession } from '../api/memberApi';
import {
  drumActivePlanAtom,
  drumLanguageAtom,
  drumLoggedInAtom,
  drumUserPreferencesAtom,
  drumUserProfileAtom,
  drumUserTokenAtom,
  type DrumUserPreferences,
  type DrumUserProfile,
  type ActivePlan,
} from '../store/drumAppAtoms';

export function applyMemberSessionData(
  data: { user?: DrumUserProfile; preferences?: DrumUserPreferences },
  setters: {
    setUserProfile: (user: DrumUserProfile | null) => void;
    setPreferences: (prefs: DrumUserPreferences) => void;
    setLanguage: (language: string) => void;
    setActivePlan: (plan: ActivePlan) => void;
  },
) {
  if (data.user) {
    setters.setUserProfile(data.user);
    if (data.user.grade) setters.setActivePlan(data.user.grade);
  }
  if (data.preferences) {
    setters.setPreferences(data.preferences);
    if (data.preferences.fromStore && data.preferences.language) setters.setLanguage(data.preferences.language);
  }
}

export function useMemberSession() {
  const [loggedIn, setLoggedIn] = useAtom(drumLoggedInAtom);
  const [token, setToken] = useAtom(drumUserTokenAtom);
  const setUserProfile = useSetAtom(drumUserProfileAtom);
  const setPreferences = useSetAtom(drumUserPreferencesAtom);
  const setLanguage = useSetAtom(drumLanguageAtom);
  const setActivePlan = useSetAtom(drumActivePlanAtom);

  useEffect(() => {
    if (!loggedIn || !token) return undefined;
    let cancelled = false;

    fetchMemberSession(token)
      .then((session) => {
        if (cancelled) return;
        applyMemberSessionData(session, { setUserProfile, setPreferences, setLanguage, setActivePlan });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const status = typeof error === 'object' && error && 'status' in error ? Number((error as { status?: number }).status) : 0;
        if (status === 401 || status === 403) {
          setLoggedIn(false);
          setToken('');
          setUserProfile(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [loggedIn, token, setActivePlan, setLanguage, setLoggedIn, setPreferences, setToken, setUserProfile]);
}
